import { StyleSheet } from 'react-native'

import { FontFamilyMetadata } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/fontFamilyMetadata'
import { ThemeColors } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/colors'
import { ComponentStyles } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/componentStyles'

import { IS_WEB } from '../constants'
import { getTypeStyles } from '../styles/typeStyles'

export const getCommonStyles = (
  colors: ThemeColors,
  fontFamilyMetadata: FontFamilyMetadata,
  componentStyles?: ComponentStyles,
) => {
  const typeStyles = getTypeStyles(colors, fontFamilyMetadata)
  return StyleSheet.create({
    header: {
      fontSize: 20,
      fontFamily: 'Moderat-Bold',
      color: colors.textPrimary,
      margin: -10,
      marginBottom: 10,
      paddingTop: 50,
      paddingLeft: 20,
      display: 'flex',
      flexDirection: 'row',
      height: 80,
      backgroundColor: colors.backgroundPrimary,
    },
    container: {
      backgroundColor: colors.backgroundSecondary,
      padding: 10,
    },
    link: {
      ...typeStyles.titleXsmall,
      color: colors.linkDefault,
    },
    linkDarkBg: {
      fontFamily: 'Moderat-Medium',
      color: colors.textInverse,
      textDecorationLine: 'underline',
    },
    textInput: {
      ...typeStyles.bodyDefault,
      color: colors.inputTextDefault,
      backgroundColor: colors.inputBackgroundDefault,
      borderColor: colors.inputOutlineDefault,
      ...componentStyles?.inputField.default,
    },
    textInputActive: {
      ...typeStyles.bodyDefault,
      ...componentStyles?.inputField.default,
      ...componentStyles?.inputField.active,
      backgroundColor: colors.inputBackgroundDefault,
      color: colors.inputTextActive,
      borderColor: colors.inputOutlineFocus,
    },
    textInputInactive: {
      ...typeStyles.bodyDefault,
      ...componentStyles?.inputField.default,
      color: colors.inputTextInactive,
      borderWidth: 0,
      backgroundColor: colors.inputBackgroundInactive,
    },
    fixedButtonContainer: {
      paddingHorizontal: 16,
      paddingVertical: 12,
      backgroundColor: colors.backgroundPrimary,
      borderTopWidth: 1,
      borderTopColor: colors.borderDefault,
    },
    flex1View: {
      flex: 1,
    },
  })
}

export const getFocusBoxShadow = ({
  innerBoxShadowColor,
  outerBoxShadowColor,
  innerBoxShadowSize = '4px',
  outerBoxShadowSize = '5.5px',
  colors,
}: {
  innerBoxShadowColor?: string
  outerBoxShadowColor?: string
  innerBoxShadowSize?: string
  outerBoxShadowSize?: string
  colors: ThemeColors
}) => {
  const componentColors = colors.components.focusBoxShadow
  return {
    ...(IS_WEB
      ? {
          boxShadow: `0 0 0 ${innerBoxShadowSize} ${
            innerBoxShadowColor || componentColors.innerBox.fill
          }, 0 0 0 ${outerBoxShadowSize} ${outerBoxShadowColor || componentColors.outerBox.fill}`,
        }
      : {}),
  }
}

export const getLinkStyle = (colors: ThemeColors, fontFamilyMetadata: FontFamilyMetadata) =>
  StyleSheet.create({
    link: {
      ...getTypeStyles(colors, fontFamilyMetadata).titleXsmall,
      color: colors.linkDefault,
    },
  })

/**
 * Helper function to get Aurora-aware background color.
 * Returns 'transparent' when Aurora theme is enabled, otherwise returns the provided fallback color.
 *
 * @param isAuroraTheme - Whether Aurora theme is enabled
 * @param fallbackColor - The color to use when Aurora theme is disabled (can be a string or a function that returns a string)
 * @param auroraColor - The color to use when Aurora theme is enabled (defaults to 'transparent')
 * @returns The background color string
 */
export const getAuroraAwareBackgroundColor = (
  isAuroraTheme: boolean,
  fallbackColor: string | (() => string),
  auroraColor: string | (() => string) = 'transparent',
): string => {
  if (isAuroraTheme) return typeof auroraColor === 'function' ? auroraColor() : auroraColor
  return typeof fallbackColor === 'function' ? fallbackColor() : fallbackColor
}

export const getInputFieldStyles = ({
  colors,
  fontFamilyMetadata,
  componentStyles,
  active,
  readOnly,
  error,
}: {
  colors: ThemeColors
  fontFamilyMetadata: FontFamilyMetadata
  componentStyles: ComponentStyles
  active?: boolean
  readOnly?: boolean
  error?: boolean
}) => {
  const commonStyles = getCommonStyles(colors, fontFamilyMetadata, componentStyles)
  let styles = { ...commonStyles.textInput }
  if (readOnly) {
    return { ...commonStyles.textInputInactive }
  }
  if (active) {
    styles = { ...commonStyles.textInputActive }
  }
  if (error) {
    styles.borderColor = colors.inputOutlineError
  }
  return styles
}
